import { useMemo, useState } from "react"
import { Input } from "@/components/ui/primitives"
import { SketchDialog } from "@/components/sketch/sketch-dialog"
import { ShotLocationAngles } from "@/components/shot-panel/shot-location-angles"
import { EntityThumbSquare } from "@/components/workspace/entity-thumb-square"
import { useTranslation } from "@/hooks/use-translation"
import {
  LOCATION_FACINGS,
  PRIMARY_SHOT_LOCATION_FACING,
  type LocationFacing,
  type LocationStorableFacing,
  type LocationViewpointRow,
} from "@/lib/locations-api"
import { locationListThumbnail } from "@/lib/location-thumbnail"
import { cn } from "@/lib/utils"
import { UI_CARD_SELECTED, UI_CHOICE_CONTROL, UI_CHOICE_CONTROL_SM, UI_NESTED_SELECTED } from "@/lib/ui-selection"

export type ShotLocationPickerRow = {
  id: number
  name: string
  setting_type?: string | null
  reference_sheet_url: string | null
  reference_sheet_download_url?: string | null
  reference_urls_by_facing?: Partial<Record<LocationStorableFacing, string | null>>
  reference_thumb_urls_by_facing?: Partial<Record<LocationStorableFacing, string | null>>
  reference_version_counts_by_facing?: Partial<Record<LocationStorableFacing, number>>
  viewpoints?: LocationViewpointRow[]
  variants: {
    id: number
    name: string
    display_name: string
    reference_url?: string | null
    reference_urls_by_facing?: Partial<Record<LocationStorableFacing, string | null>>
    reference_thumb_urls_by_facing?: Partial<Record<LocationStorableFacing, string | null>>
    reference_download_urls_by_facing?: Partial<Record<LocationStorableFacing, string | null>>
    reference_version_counts_by_facing?: Partial<Record<LocationStorableFacing, number>>
    viewpoints?: LocationViewpointRow[]
    is_default?: boolean
  }[]
}

export function normalizeShotLocationFacing(raw: string | null | undefined): LocationFacing {
  if (raw && (LOCATION_FACINGS as readonly string[]).includes(raw)) return raw as LocationFacing
  return PRIMARY_SHOT_LOCATION_FACING
}

export function findLocationByVariantId(
  locations: ShotLocationPickerRow[],
  variantId: number | null,
): ShotLocationPickerRow | null {
  if (variantId == null) return null
  return locations.find((loc) => loc.variants.some((v) => v.id === variantId)) ?? null
}

function viewpointsForSelection(
  loc: ShotLocationPickerRow | null | undefined,
  variant: ShotLocationPickerRow["variants"][number] | null | undefined,
): LocationViewpointRow[] {
  const base = loc?.viewpoints ?? []
  const variantVps = variant?.viewpoints ?? []
  const byId = new Map<number, LocationViewpointRow>()
  for (const vp of [...base, ...variantVps]) byId.set(vp.id, vp)
  return Array.from(byId.values())
}

export type ShotLocationPickerProps = {
  projectId: number
  projectLocations: ShotLocationPickerRow[]
  selectedLocationId: number | null
  selectedVariantId: number | null
  selectedFacing: LocationFacing
  selectedViewpointId: number | null
  onToggleLocation: (locationId: number) => void
  onSelectVariant: (variantId: number) => void
  onSelectFacing: (facing: LocationFacing) => void
  onSelectViewpoint: (viewpoint: LocationViewpointRow) => void
  onOpenRoomViewer?: () => void
  /** When true, location/variant thumbs open Sketch view (shot panel). Dialog keeps plain thumbs. */
  enableImagePreview?: boolean
  searchClassName?: string
  listClassName?: string
  anglesClassName?: string
}

export function ShotLocationPicker({
  projectId,
  projectLocations,
  selectedLocationId,
  selectedVariantId,
  selectedFacing,
  selectedViewpointId,
  onToggleLocation,
  onSelectVariant,
  onSelectFacing,
  onSelectViewpoint,
  onOpenRoomViewer,
  enableImagePreview = false,
  searchClassName,
  listClassName,
  anglesClassName,
}: ShotLocationPickerProps) {
  const { t } = useTranslation(["shot"])
  const [filter, setFilter] = useState("")
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(null)

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    return q ? projectLocations.filter((r) => r.name.toLowerCase().includes(q)) : projectLocations
  }, [filter, projectLocations])

  const selectedVariant = useMemo(() => {
    if (selectedVariantId == null) return null
    for (const loc of projectLocations) {
      const v = loc.variants.find((x) => x.id === selectedVariantId)
      if (v) return v
    }
    return null
  }, [projectLocations, selectedVariantId])

  const selectedLocation = useMemo(
    () => projectLocations.find((l) => l.id === selectedLocationId) ?? null,
    [projectLocations, selectedLocationId],
  )

  const angleViewpoints = useMemo(
    () => viewpointsForSelection(selectedLocation, selectedVariant),
    [selectedLocation, selectedVariant],
  )

  return (
    <>
      <div className={cn("shrink-0 border-b border-ui-border", searchClassName)}>
        <Input
          placeholder={t("placeholders.searchByName")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      <div className={cn("it2tv-scrollbar min-h-0 flex-1 overflow-y-auto", listClassName)}>
        {filtered.length === 0 ? (
          <p className="text-sm text-ui-text-muted">
            {projectLocations.length === 0 ? t("location.noLocations") : t("location.nothingFound")}
          </p>
        ) : (
          <ul className="space-y-2">
            {filtered.map((loc) => {
              const on = selectedLocationId === loc.id
              const locThumb = locationListThumbnail(loc)
              return (
                <li key={loc.id}>
                  <div
                    className={cn(
                      "flex items-start gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors",
                      on
                        ? UI_CARD_SELECTED
                        : "border-ui-border bg-ui-surface-raised text-ui-text-secondary hover:border-ui-border-strong hover:bg-ui-bg-elevated/[0.05]",
                    )}
                  >
                    <label className="flex cursor-pointer items-center gap-3 pt-0.5">
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => onToggleLocation(loc.id)}
                        className={UI_CHOICE_CONTROL}
                      />
                    </label>
                    {locThumb ? (
                      enableImagePreview ? (
                        <button
                          type="button"
                          onClick={() =>
                            setPreview({
                              url: (loc.reference_sheet_download_url ?? loc.reference_sheet_url ?? locThumb) as string,
                              title: loc.name,
                            })
                          }
                          className="shrink-0 cursor-zoom-in rounded-lg focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
                          aria-label={t("location.openLocationImage", { name: loc.name })}
                        >
                          <img
                            src={locThumb}
                            alt=""
                            className="h-10 w-10 rounded-lg border border-ui-border object-cover"
                          />
                        </button>
                      ) : (
                        <img
                          src={locThumb}
                          alt=""
                          className="h-10 w-10 shrink-0 rounded-lg border border-ui-border object-cover"
                        />
                      )
                    ) : (
                      <EntityThumbSquare name={loc.name} size="compact" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 truncate font-medium text-ui-text-primary">
                        <span className="truncate">{loc.name}</span>
                        {loc.setting_type && (
                          <span className="shrink-0 rounded bg-ui-surface-raised px-1 py-px text-[10px] leading-tight font-semibold text-ui-text-secondary uppercase">
                            {loc.setting_type}
                          </span>
                        )}
                      </div>
                      {on && loc.variants.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {loc.variants.map((v) => {
                            const isActive = selectedVariantId === v.id
                            const variantThumb = locationListThumbnail(v)
                            return (
                              <label
                                key={v.id}
                                className={cn(
                                  "flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors",
                                  isActive
                                    ? UI_NESTED_SELECTED
                                    : "text-ui-text-secondary hover:bg-ui-bg-elevated/[0.04] hover:text-ui-text-primary",
                                )}
                              >
                                <input
                                  type="radio"
                                  name={`loc_variant_${loc.id}`}
                                  checked={isActive}
                                  onChange={() => onSelectVariant(v.id)}
                                  className={UI_CHOICE_CONTROL_SM}
                                />
                                {variantThumb ? (
                                  enableImagePreview ? (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        const fullUrl =
                                          v.reference_download_urls_by_facing?.[PRIMARY_SHOT_LOCATION_FACING] ??
                                          v.reference_url ??
                                          variantThumb
                                        setPreview({ url: fullUrl as string, title: v.display_name })
                                      }}
                                      className="shrink-0 cursor-zoom-in rounded-md focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)] focus-visible:outline-none"
                                      aria-label={t("location.openImage", { name: v.display_name })}
                                    >
                                      <img
                                        src={variantThumb}
                                        alt=""
                                        className="h-8 w-8 rounded-md border border-ui-border object-cover"
                                      />
                                    </button>
                                  ) : (
                                    <img
                                      src={variantThumb}
                                      alt=""
                                      className="h-8 w-8 shrink-0 rounded-md border border-ui-border object-cover"
                                    />
                                  )
                                ) : (
                                  <EntityThumbSquare name={v.display_name} size="radio" />
                                )}
                                <span className="truncate text-xs font-medium">{v.display_name}</span>
                              </label>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className={cn("shrink-0 border-t border-ui-border", anglesClassName)}>
        {selectedVariantId != null ? (
          <ShotLocationAngles
            projectId={projectId}
            selectedFacing={selectedFacing}
            selectedViewpointId={selectedViewpointId}
            referenceUrlsByFacing={selectedVariant?.reference_urls_by_facing}
            referenceThumbUrlsByFacing={selectedVariant?.reference_thumb_urls_by_facing}
            referenceDownloadUrlsByFacing={selectedVariant?.reference_download_urls_by_facing}
            primaryFallbackUrl={selectedVariant?.reference_url ?? selectedLocation?.reference_sheet_url}
            primaryFallbackDownloadUrl={
              selectedVariant?.reference_download_urls_by_facing?.[PRIMARY_SHOT_LOCATION_FACING] ??
              selectedLocation?.reference_sheet_download_url ??
              null
            }
            viewpoints={angleViewpoints}
            onSelectFacing={onSelectFacing}
            onSelectViewpoint={onSelectViewpoint}
            onOpenRoomViewer={onOpenRoomViewer}
          />
        ) : (
          <p className="text-xs text-ui-text-muted">{t("location.facingHint")}</p>
        )}
      </div>

      {enableImagePreview ? (
        <SketchDialog
          open={!!preview}
          onOpenChange={(o) => !o && setPreview(null)}
          projectId={projectId}
          sourceUrl={preview?.url ?? null}
          presentation="view"
          allowEdit={false}
          persist="none"
          nested
          headerTitle={preview?.title}
        />
      ) : null}
    </>
  )
}
